import { logInfo } from './coralogix-rum';

interface LatencyMetrics {
  startTime: number;
  endTime?: number;
  duration?: number;
}

interface APMLatencyData {
  [key: string]: LatencyMetrics;
}

class APMLatencyTracker {
  private latencyData: APMLatencyData = {};
  private appStartTime: number | null = null;

  init() {
    if (this.appStartTime === null) {
      this.appStartTime = performance.now();
    }
  }

  startTracking(componentName: string, paymentMethodId?: string) {
    const key = this.getKey(componentName, paymentMethodId);
    
    this.latencyData[key] = {
      startTime: performance.now(),
    };

    console.log(`[APM Latency] Started tracking: ${key}`);
  }

  endTracking(
    componentName: string,
    paymentMethodId?: string,
    additionalData?: Record<string, any>
  ) {
    const key = this.getKey(componentName, paymentMethodId);
    const metrics = this.latencyData[key];

    if (!metrics) {
      console.warn(`[APM Latency] No start time found for: ${key}`);
      return;
    }

    metrics.endTime = performance.now();
    metrics.duration = metrics.endTime - metrics.startTime;

    const timeFromAppStart = this.appStartTime 
      ? metrics.endTime - this.appStartTime 
      : undefined;

    console.log(
      `[APM Latency] ${key} loaded in ${(metrics.duration / 1000).toFixed(3)}s`
    );

    logInfo('APM Component Load Time', {
      componentName,
      paymentMethodId,
      loadTime: metrics.duration,
      loadTimeSeconds: parseFloat((metrics.duration / 1000).toFixed(3)),
      timeFromAppStart,
      timeFromAppStartSeconds: timeFromAppStart 
        ? parseFloat((timeFromAppStart / 1000).toFixed(3)) 
        : undefined,
      ...additionalData,
    }, {
      metric_type: 'apm_load_latency',
      component: componentName,
    });

    delete this.latencyData[key];
  }

  track(componentName: string, paymentMethodId?: string) {
    this.startTracking(componentName, paymentMethodId);
    
    return (additionalData?: Record<string, any>) => {
      this.endTracking(componentName, paymentMethodId, additionalData);
    };
  }

  logInstant(
    componentName: string,
    duration: number,
    additionalData?: Record<string, any>
  ) {
    console.log(
      `[APM Latency] ${componentName} completed in ${(duration / 1000).toFixed(3)}s`
    );

    logInfo('APM Component Load Time', {
      componentName,
      loadTime: duration,
      loadTimeSeconds: parseFloat((duration / 1000).toFixed(3)),
      ...additionalData,
    }, {
      metric_type: 'apm_load_latency',
      component: componentName,
    });
  }

  private getKey(componentName: string, paymentMethodId?: string): string {
    return paymentMethodId ? `${componentName}-${paymentMethodId}` : componentName;
  }

  getLatencyData(): APMLatencyData {
    return { ...this.latencyData };
  }

  clear() {
    this.latencyData = {};
  }
}

export const apmLatencyTracker = new APMLatencyTracker();

export const useAPMLatencyTracker = () => apmLatencyTracker;

